import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { useLocation } from "react-router-dom";

import PageTopHelpButton from "./PageTopHelpButton";
import { productForRoute } from "./routeMatch";

// Products that surface a prominent "How to use X" pill in the header slot. Add
// an entry to enable the pill on that product's pages.
const PRODUCT_HELP_LABELS: Record<string, string> = {
  "equipment-ai": "Equipment AI",
};

// The global header (src/containers/TheHeader.jsx) renders an empty element with
// this id beside the breadcrumb. We portal the pill into it so the header owns
// the placement and alignment — no DOM splicing, no styling assumptions here.
const HEADER_PILL_SLOT_ID = "hdr-pill-slot";

// Resolve the header's pill slot once it's in the DOM (only while `active`). The
// slot is rendered by TheHeader, so this just locates it — the effect re-runs
// after mount so the first paint picks it up.
function useHeaderPillSlot(active: boolean): HTMLElement | null {
  const [slot, setSlot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setSlot(active ? document.getElementById(HEADER_PILL_SLOT_ID) : null);
  }, [active]);

  return slot;
}

/**
 * Fills the global header's pill slot with the "How to use X" pill, via a
 * portal. TheLayout mounts this once so the pill can live in the header without
 * TheHeader importing the help feature or owning the route gating — TheHeader
 * contributes only the empty slot. Route-gated through productForRoute +
 * PRODUCT_HELP_LABELS, so the pill appears only on products that opt in.
 */
export default function GlobalHeaderHelpPill() {
  const { pathname } = useLocation();
  const product = productForRoute(pathname);
  const productLabel = PRODUCT_HELP_LABELS[product];
  const slot = useHeaderPillSlot(!!productLabel);

  if (!slot || !productLabel) return null;

  return createPortal(
    <span data-testid="hdr-product-help">
      <PageTopHelpButton productLabel={productLabel} product={product} />
    </span>,
    slot,
  );
}
